import { put, takeLatest } from 'redux-saga/effects';
import get from 'lodash/get';
import axiosConfig from './axiosConfig';
import {
  GET_ALL_COATING_PUMPHOUSE,
  BULK_SAVE_COATING_PUMPHOUSE,
  SAVE_PUMP_HOUSE_LIMIT_CONFIG,
} from './types';
import {
  getAllCoatingPumphouseSuccess,
  getAllCoatingPumphouseFailure,
  bulkSaveCoatingPumphouseSuccess,
  bulkSaveCoatingPumphouseFailure,
  getAllCoatingPumphouse,
  savePumpHouseLimitConfigSuccess,
  savePumpHouseLimitConfigFailure,
} from './actions';
import { Notification as customNotification } from '../../components/ui/custom-notification';

const DEFAULT_LIMIT_OBJ = {
  waterMin: 66,
  waterMax: 75,
  baseMin: 74,
  baseMax: 78,
  hardenerMin: 36,
  hardenerMax: 40,
};

function pickNum(v, fallback) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

/** Coerce to number when safe (e.g. 3488), else pass through for string IDs. */
function normalizeProjectIdForApi(projectId) {
  if (projectId === undefined || projectId === null || projectId === '') return undefined;
  const n = Number(projectId);
  return Number.isFinite(n) ? n : projectId;
}

/**
 * Normalizes getPumpHouseLimitConfig / save response into
 * `{ limits, active, projectId, pipeId }` or null.
 */
function normalizePumpHouseLimitConfig(raw) {
  if (raw == null || raw === '') return null;
  const root = typeof raw === 'object' ? raw : {};
  const cfg = root.config && typeof root.config === 'object' ? root.config : root;
  if (!cfg || typeof cfg !== 'object') return null;
  const d = DEFAULT_LIMIT_OBJ;
  const limits = {
    waterMin: pickNum(cfg.waterMin ?? cfg.water_min, d.waterMin),
    waterMax: pickNum(cfg.waterMax ?? cfg.water_max, d.waterMax),
    baseMin: pickNum(cfg.baseMin ?? cfg.base_min, d.baseMin),
    baseMax: pickNum(cfg.baseMax ?? cfg.base_max, d.baseMax),
    hardenerMin: pickNum(cfg.hardenerMin ?? cfg.hardener_min, d.hardenerMin),
    hardenerMax: pickNum(cfg.hardenerMax ?? cfg.hardener_max, d.hardenerMax),
  };
  const active = !!(cfg.limitsActive ?? cfg.limits_active ?? cfg.active);
  const projectId = cfg.projectId ?? cfg.project_id ?? null;
  const pipeId = cfg.pipeId ?? cfg.pipe_id ?? null;
  return { limits, active, projectId, pipeId };
}

// Maps a snake_case API record to the camelCase CoatingRow shape.
//
// Two event-row formats exist in the DB:
//  Format A (via direct API insert): is_event=true,  event_message="...", joint=null
//  Format B (via CSV upload):        is_event=false, event_message=null,  joint="Emergency Stop Actuated"
//
// Format B is detected the same way the CSV parser does: joint is a non-null,
// non-numeric string that isn't "pass"/"fail".
//
// Format C (API quirk): is_event=false but event_message is set (e.g. "Stall Test Failed- Upstroke Leg A"
// with a numeric joint). Those rows must still be treated as events so fault charts and drilldown see the message.
const mapApiRecord = (r) => {
  const jointVal = r.joint ?? '';
  const msgRaw = String(r.event_message ?? '').trim();
  const hasApiEventMessage = msgRaw !== '';

  const isEventFromJoint =
    !r.is_event &&
    !hasApiEventMessage &&
    jointVal !== '' &&
    isNaN(Number(jointVal)) &&
    jointVal.toLowerCase() !== 'pass' &&
    jointVal.toLowerCase() !== 'fail';

  const isEvent =
    !!r.is_event || isEventFromJoint || hasApiEventMessage;
  const eventMsg = hasApiEventMessage
    ? msgRaw
    : (isEventFromJoint ? jointVal : undefined);

  return {
    id:              r.id,
    projectId:       r.project_id,
    pipeId:          r.pipe_id,
    user:            r.user             ?? '',
    coatingMaterial: r.coating_material ?? '',
    coatingType:     r.coating_type     ?? '',
    baseBatch:       r.base_batch       ?? '',
    hardenerBatch:   r.hardener_batch   ?? '',
    jointWidth:      r.joint_width      ?? '',
    jointThickness:  r.joint_thickness  ?? '',
    prime:           r.prime            ?? '',
    flush:           r.flush            ?? '',
    date:            r.date             ?? '',
    time:            r.time             ?? '',
    joint:           isEvent ? '' : jointVal,
    waterTemp:       r.water_temp       ?? '',
    baseTemp:        r.base_temp        ?? '',
    hardenerTemp:    r.hardener_temp    ?? '',
    ambientTemp:     r.ambient_temp     ?? '',
    humidity:        r.humidity         ?? '',
    dewPoint:        r.dew_point        ?? '',
    downstrokeStall: r.downstroke_stall ?? '',
    upstrokeStall:   r.upstroke_stall   ?? '',
    stallTestType:   r.stall_test_type  ?? '',
    systemFlushed:   r.system_flushed   ?? '',
    systemPrimed:    r.system_primed    ?? '',
    valveNumber:     r.valve_number     ?? '',
    fittingNumber:   r.fitting_number   ?? '',
    isEvent,
    eventMessage:    eventMsg,
    s3Link:          r.s3_link ?? r.s3Link ?? '',
  };
};

function* getAllCoatingPumphouseSaga({ payload = {} }) {
  try {
    const { projectId, pipeId } = payload;
    let url = '/coatingPumphouse/getAllCoatingPumphouse';
    const params = [];
    if (projectId !== undefined && projectId !== null && projectId !== '') {
      params.push(`project_id=${projectId}`);
    }
    if (pipeId !== undefined && pipeId !== null && pipeId !== '') {
      params.push(`pipe_id=${pipeId}`);
    }
    if (params.length > 0) url += `?${params.join('&')}`;

    const res = yield axiosConfig.get(url);

    if (res.status === 200) {
      // Response: { data: [...records], total, message }
      const rawList = get(res, ['data', 'data', 'data'], get(res, 'data', {}));
      const rows = Array.isArray(rawList) ? rawList?.map(mapApiRecord) : [];

      let limitConfig = null;
      if (projectId !== undefined && projectId !== null && projectId !== '') {
        try {
          const limUrl = `/coatingPumphouse/getPumpHouseLimitConfig?projectId=${encodeURIComponent(String(projectId))}`;
          const limRes = yield axiosConfig.get(limUrl);
          if (limRes.status === 200) {
            const rawLim = get(limRes, ['data', 'data', 'data'], get(limRes, ['data', 'data'], get(limRes, 'data', null)));
            limitConfig = normalizePumpHouseLimitConfig(rawLim);
          }
        } catch (limErr) {
          const st = limErr?.response?.status;
          if (st !== 404) {
            // 404 = no saved config yet; other errors are non-fatal for the main table load
          }
          limitConfig = null;
        }
      }

      yield put(getAllCoatingPumphouseSuccess({ rows, limitConfig }));
    } else {
      yield put(getAllCoatingPumphouseFailure('Failed to fetch coating pumphouse data'));
    }
  } catch (error) {
    const message = error?.response?.data?.message || error?.message || 'Failed to fetch coating pumphouse data';
    yield put(getAllCoatingPumphouseFailure(message));
  }
}

/**
 * POST `/coatingPumphouse/savePumpHouseLimitConfig`
 * Body (top-level): { projectId, userEmailId, userId?, config?: { … } }
 * @see curl … -d "{\"projectId\":3488,\"userEmailId\":\"user@company.com\"}"
 */
function* savePumpHouseLimitConfigSaga({ payload = {} }) {
  try {
    const {
      userEmailId,
      userId,
      projectId,
      pipeId,
      limits,
      active,
      maxRows = 5000,
      defaultPageSize = 100,
    } = payload;

    if (!userEmailId || String(userEmailId).trim() === '') {
      customNotification.error('Cannot save limits', 'User email is not available. Please sign in again.');
      yield put(savePumpHouseLimitConfigFailure('Missing userEmailId'));
      return;
    }
    const projectIdForApi = normalizeProjectIdForApi(projectId);
    if (projectIdForApi === undefined) {
      customNotification.error('Cannot save limits', 'Project ID is required.');
      yield put(savePumpHouseLimitConfigFailure('Missing projectId'));
      return;
    }
    if (!limits || typeof limits !== 'object') {
      yield put(savePumpHouseLimitConfigFailure('Invalid limits'));
      return;
    }

    const url = '/coatingPumphouse/savePumpHouseLimitConfig';
    const body = {
      projectId: projectIdForApi,
      userEmailId: String(userEmailId).trim(),
      config: {
        maxRows: Number(maxRows) || 5000,
        defaultPageSize: Number(defaultPageSize) || 100,
        limitsActive: !!active,
        waterMin: limits.waterMin,
        waterMax: limits.waterMax,
        baseMin: limits.baseMin,
        baseMax: limits.baseMax,
        hardenerMin: limits.hardenerMin,
        hardenerMax: limits.hardenerMax,
        pipeId: pipeId != null && pipeId !== '' ? pipeId : null,
      },
    };
    if (userId != null && String(userId).trim() !== '') {
      body.userId = String(userId).trim();
    }

    const res = yield axiosConfig.post(url, body);

    if (res.status === 200 || res.status === 201) {
      const bodyOut = get(res, ['data', 'data'], get(res, 'data', {}));
      yield put(savePumpHouseLimitConfigSuccess({
        saved: bodyOut,
        limitConfig: {
          limits: {
            waterMin: limits.waterMin,
            waterMax: limits.waterMax,
            baseMin: limits.baseMin,
            baseMax: limits.baseMax,
            hardenerMin: limits.hardenerMin,
            hardenerMax: limits.hardenerMax,
          },
          active: !!active,
          projectId: projectId != null && projectId !== '' ? projectId : null,
          pipeId: pipeId != null && pipeId !== '' ? pipeId : null,
        },
      }));
      customNotification.success(active ? 'Temperature limits saved' : 'Temperature limits cleared');
    } else {
      customNotification.error('Failed to save pump house limit config');
      yield put(savePumpHouseLimitConfigFailure('Failed to save pump house limit config'));
    }
  } catch (error) {
    const message = error?.response?.data?.message || error?.message || 'Failed to save pump house limit config';
    customNotification.error(message);
    yield put(savePumpHouseLimitConfigFailure(message));
  }
}

function* bulkSaveCoatingPumphouseSaga({ payload = {} }) {
  try {
    const url = '/coatingPumphouse/bulkCreateUpdateCoatingPumphouse';
    const res = yield axiosConfig.post(url, payload);

    if (res.status === 200 || res.status === 201) {
      const body = get(res, ['data', 'data'], get(res, 'data', {}));
      yield put(bulkSaveCoatingPumphouseSuccess(body));
      customNotification.success('Coating data saved successfully');
      // Re-fetch so the table shows the freshly saved records
      const rows = payload.items ?? payload.rows ?? [];
      const firstRow = rows[0] ?? {};
      const projectId = firstRow.projectId ?? firstRow.project_id ?? '';
      const pipeId    = firstRow.pipeId    ?? firstRow.pipe_id    ?? '';
      yield put(getAllCoatingPumphouse({ projectId, pipeId }));
    } else {
      customNotification.error('Failed to save coating pumphouse data');
      yield put(bulkSaveCoatingPumphouseFailure('Failed to save coating pumphouse data'));
    }
  } catch (error) {
    const message = error?.response?.data?.message || error?.message || 'Failed to save coating pumphouse data';
    customNotification.error(message);
    yield put(bulkSaveCoatingPumphouseFailure(message));
  }
}

function* coatingPumphouseWatcher() {
  yield takeLatest(GET_ALL_COATING_PUMPHOUSE, getAllCoatingPumphouseSaga);
  yield takeLatest(BULK_SAVE_COATING_PUMPHOUSE, bulkSaveCoatingPumphouseSaga);
  yield takeLatest(SAVE_PUMP_HOUSE_LIMIT_CONFIG, savePumpHouseLimitConfigSaga);
}

export default function* coatingPumphouseSaga() {
  yield coatingPumphouseWatcher();
}
